// Precarga de datos de referencia para reglas: países, couriers, integraciones y servicios (evita re-fetch al abrir el formulario o condiciones).
import { createHttpClient } from '@adapters/HttpClient';
import { apiClientWH } from '@shared/services';
import globalCache from '@shared/lib/GlobalCache';
import { ConditionApiAdapter } from '../adapters/ConditionApiAdapter';
import { CourierIntegrationApiAdapter, IntegrationOption } from '../adapters/CourierIntegrationApiAdapter';

/** Misma clave usada al hidratar integraciones desde un `getAll` fuera del prefetch. */
export const COURIER_INTEGRATIONS_LIST_CACHE_KEY = 'courierIntegrations:all';
const COURIERS_CACHE_KEY = 'ref:couriers';

export type CouriersListEntry = { id: string | number; name: string; [k: string]: unknown };

let prefetchInFlight: Promise<void> | null = null;

function parseList<T = unknown>(raw: any): T[] {
  if (Array.isArray(raw)) return raw as T[];
  if (raw?.data && Array.isArray(raw.data)) return raw.data as T[];
  return [];
}

/**
 * Carga en paralelo: países (condición), lista de couriers, integraciones y servicios de cada integración.
 * Usa claves alineadas con `useShippingRuleForm` / `loadValues` / `loadCourierProducts`.
 * Idempotente: varias llamadas comparten la misma promesa hasta que termine.
 */
export function prefetchRulesReferenceData(): Promise<void> {
  if (prefetchInFlight) {
    return prefetchInFlight;
  }

  const http = createHttpClient(apiClientWH);
  const condAdapter = new ConditionApiAdapter(http);
  const courierIntAdapter = new CourierIntegrationApiAdapter(http);

  prefetchInFlight = (async () => {
    // 1) Valores de desplegables usados en condiciones (misma clave que loadValues)
    const valueFields = ['shipping_country_id', 'product_id', 'pack_id', 'payment_method_id', 'shop_id'] as const;
    await Promise.all(
      valueFields.map((field) => globalCache.getOrSet(`values:${field}`, () => condAdapter.getConditionValues(field))),
    );

    // 2) Couriers
    if (!globalCache.get(COURIERS_CACHE_KEY)) {
      const raw = await http.get<any>('/couriers');
      const list = parseList<CouriersListEntry>(raw);
      globalCache.set(COURIERS_CACHE_KEY, list);
    }

    // 3) Integraciones (una sola instancia: alimenta caché interna del adaptador y GlobalCache)
    const integrations = await courierIntAdapter.getAll();
    globalCache.set(COURIER_INTEGRATIONS_LIST_CACHE_KEY, integrations as IntegrationOption[]);

    // 4) Servicios de cada integración (misma clave que loadCourierProducts)
    await Promise.allSettled(
      integrations.map((i) =>
        globalCache.getOrSet(`courierProducts:${i.id}`, () => courierIntAdapter.getProducts(i.id)),
      ),
    );
  })().catch((e) => {
    prefetchInFlight = null;
    throw e;
  });

  return prefetchInFlight;
}

export function getCachedCourierIntegrations(): IntegrationOption[] | undefined {
  return globalCache.get<IntegrationOption[]>(COURIER_INTEGRATIONS_LIST_CACHE_KEY);
}

export function getCachedCouriers(): CouriersListEntry[] | undefined {
  return globalCache.get<CouriersListEntry[]>(COURIERS_CACHE_KEY);
}
